import { InternationalisationService } from "../../forked/angular-internationalisation";
import Utils from "../utils";
import { ApiResponseData } from "../../services/YourAppServer/YourAppServerApi.service";

/** Account payment activation status. */
export enum AccountPaymentActivationStatus {
  None = "none",
  Pending = "pending",
  Rejected = "rejected",
  Approved = "approved"
}

/** Display state of the account-payment row in the payment-method list. */
export enum AccountPaymentRowState {
  Hidden = "hidden",
  RequestInFlight = "requestInFlight",
  Pending = "pending",
  RequestDialog = "requestDialog",
  LimitBlocked = "limitBlocked",
  Selectable = "selectable"
}

/** All inputs of {@link resolveAccountPaymentRowState}. */
export interface AccountPaymentRowStateInput {
  isUserRegistered: boolean;
  isEnabledForOrdertype: boolean;
  /** Tenant flag `show_hs_accounting_customer`. */
  isCustomerPaymentOffered: boolean;
  isLimitBlocked: boolean;
  /** The activation status was successfully loaded from the server. */
  isStatusLoaded: boolean;
  status: AccountPaymentActivationStatus;
  isRequestInFlight: boolean;
}

/** Caller-supplied part of the row-state input; the service owns the remaining fields. */
export type AccountPaymentRowStateContext = Pick<AccountPaymentRowStateInput,
  "isUserRegistered" | "isEnabledForOrdertype" | "isCustomerPaymentOffered" | "isLimitBlocked">;

/** Data contract of the account-payment request bottom sheet. `undefined` label = dialog default. */
export interface AccountPaymentRequestDialogData {
  cancelButtonLangkey?: string;
}

/** Default langkey of the dialog's cancel button; owned by the dialog component. */
export const ACCOUNT_PAYMENT_DIALOG_DEFAULT_CANCEL_LANGKEY = "phGlobal_Return";

/** Cancel-button langkey used by the checkout to offer switching the payment method. */
export const ACCOUNT_PAYMENT_DIALOG_CHANGE_PAYMENT_METHOD_LANGKEY = "phAccountPayment_ButtonChangePaymentMethod";

/** Resolves the account-payment row state; a blocked limit wins over flag-off hiding for approved accounts. */
export function resolveAccountPaymentRowState(input: AccountPaymentRowStateInput): AccountPaymentRowState {
  if (!input.isEnabledForOrdertype || !input.isUserRegistered || !input.isStatusLoaded) {
    return AccountPaymentRowState.Hidden;
  }
  if (input.isRequestInFlight) {
    return AccountPaymentRowState.RequestInFlight;
  }
  if (input.status === AccountPaymentActivationStatus.Pending) {
    return AccountPaymentRowState.Pending;
  }
  if (input.status === AccountPaymentActivationStatus.None || input.status === AccountPaymentActivationStatus.Rejected) {
    return AccountPaymentRowState.RequestDialog;
  }
  if (input.status === AccountPaymentActivationStatus.Approved) {
    if (input.isLimitBlocked) {
      return AccountPaymentRowState.LimitBlocked;
    }
    return input.isCustomerPaymentOffered ? AccountPaymentRowState.Selectable : AccountPaymentRowState.Hidden;
  }
  return AccountPaymentRowState.Hidden;
}

/** GetAccountingStatus response (route: account/GetAccountingStatus). */
export class GetAccountPaymentActivationStatusDataModel implements ApiResponseData {
  /** Observed wire values: "none" | "pending" | "rejected" | "approved" (lowercase enum strings). */
  public status: string = null;
  public DeserializeType(): void { }
}

/** RequestAccountingActivation response — server returns AccountingStatusModel { status }. */
export class RequestAccountPaymentActivationDataModel implements ApiResponseData {
  public status: string = null;
  public DeserializeType(): void { }
}

/** GetAccountingInfo response (route: account/GetAccountingInfo).
 *  Wire fields are integer cents per AccountingInfoModel on the server. */
export class AccountPaymentCreditDataModel implements ApiResponseData {
  public credit_limit_cents: number = null;
  public used_cents: number = null;
  public available_cents: number = null;

  public DeserializeType(): void { }
}

/** Account payment credit values. */
export interface AccountPaymentCreditValues {
  available: number;
  usedBalance: number;
  limit: number;
}

/** Account payment limit result. */
export interface AccountPaymentLimitResult {
  /** `isLimitReached || isOverLimit` — payment option is not selectable. */
  isBlocked: boolean;
  /** The user has no remaining available credit. */
  isLimitReached: boolean;
  /** Available credit exists but the basket exceeds it. */
  isOverLimit: boolean;
  /** Over-limit overage in euros; `0` otherwise. */
  exceedAmount: number;
}

/** Checks whether the basket exceeds the available credit. */
export function evaluateAccountPaymentLimit(values: AccountPaymentCreditValues | null, basketTotal: number): AccountPaymentLimitResult {
  if (values == null) {
    return { isBlocked: false, isLimitReached: false, isOverLimit: false, exceedAmount: 0 };
  }
  // fall back to limit - usedBalance when available is not supplied
  const available = (values.available != null) ? values.available : (values.limit - values.usedBalance);
  const isLimitReached = available <= 0;
  const isOverLimit = !isLimitReached && basketTotal > available;
  const exceedAmount = isOverLimit ? (basketTotal - available) : 0;
  const isBlocked = isLimitReached || isOverLimit;
  return { isBlocked, isLimitReached, isOverLimit, exceedAmount };
}

export class AccountingCustomerDataModel {

  /** Tenant-wide default credit limit from the wire object (integer cents). Zero / null = not configured. */
  public default_credit_limit_cents: number = null;

  /** Default credit limit converted to euros; falls back to 0 when the wire value is absent. */
  public get defaultCreditLimit(): number {
    const me = this;
    return Utils.centsToCurrency(me.default_credit_limit_cents != null ? me.default_credit_limit_cents : 0);
  }

  public label_credit_range_de: string;
  public label_credit_range_en: string;
  public get creditRangeLabel(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.label_credit_range_en)
      ? me.label_credit_range_de : me.label_credit_range_en;
  }

  public label_credit_limit_de: string;
  public label_credit_limit_en: string;
  public get creditLimitLabel(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.label_credit_limit_en)
      ? me.label_credit_limit_de : me.label_credit_limit_en;
  }

  public label_used_de: string;
  public label_used_en: string;
  public get usedBalanceLabel(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.label_used_en)
      ? me.label_used_de : me.label_used_en;
  }

  public label_available_de: string;
  public label_available_en: string;
  public get availableLabel(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.label_available_en)
      ? me.label_available_de : me.label_available_en;
  }

  public auto_update_hint_de: string;
  public auto_update_hint_en: string;
  public get availableCreditAutoUpdate(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.auto_update_hint_en)
      ? me.auto_update_hint_de : me.auto_update_hint_en;
  }

  public hint_text_de: string;
  public hint_text_en: string;
  public get notActivatedDescription(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.hint_text_en)
      ? me.hint_text_de : me.hint_text_en;
  }

  public dialog_title_de: string;
  public dialog_title_en: string;
  public get activationDialogTitle(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.dialog_title_en)
      ? me.dialog_title_de : me.dialog_title_en;
  }

  public dialog_text_de: string;
  public dialog_text_en: string;
  public get activationDialogBody(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.dialog_text_en)
      ? me.dialog_text_de : me.dialog_text_en;
  }

  public pending_status_text_de: string;
  public pending_status_text_en: string;
  public get activationPendingStatus(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.pending_status_text_en)
      ? me.pending_status_text_de : me.pending_status_text_en;
  }

  public limit_blocked_hint_de: string;
  public limit_blocked_hint_en: string;
  public get limitReachedPaymentBlockedHint(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.limit_blocked_hint_en)
      ? me.limit_blocked_hint_de : me.limit_blocked_hint_en;
  }

  public over_limit_hint_de: string;
  public over_limit_hint_en: string;
  public get overLimitHint(): string {
    const me = this;
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(me.over_limit_hint_en)
      ? me.over_limit_hint_de : me.over_limit_hint_en;
  }

}
